/**
 * Tutorial Quests Component for Heat Pump Simulation
 * Provides guided learning through progressive, journey-based quests
 * 
 * @module TutorialQuests
 * @description A comprehensive tutorial system that guides learners through
 * heat pump system design, optimization, and energy efficiency concepts.
 * Each quest represents a learning journey with multiple steps, building
 * upon previous knowledge to develop mastery.
 * 
 * Supports three user types with tailored learning paths:
 * - Installer: Practical installation, commissioning, and hydraulic balancing
 * - Energy Consultant: Building analysis, COP optimization, cost/benefit analysis
 * - Sales Person: Customer communication, efficiency benefits, value proposition
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Typography, Space, Button, Checkbox, Divider, Progress, Tag, Tooltip, Collapse, Select, Input, Tabs } from "antd";
import { 
  ArrowRightOutlined,
  CheckCircleFilled, 
  BookOutlined, 
  BulbOutlined,
  TrophyOutlined, 
  ExperimentOutlined,
  RocketOutlined,
  AimOutlined,
  PlayCircleOutlined,
  CloseOutlined,
  MinusOutlined,
  ExpandOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
  ToolOutlined,
  CalculatorOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { TutorialSpotlight } from "./TutorialSpotlight";
import { TutorialQuiz } from "./TutorialQuiz";
import { ProfessionalQuiz } from "./ProfessionalQuiz";
import { AssignedSkillQuizModal } from "./AssignedSkillQuizModal";
import { getQuestsForUserType } from "./QuestDefinitions";
import { renderQuestIcon } from "./questIcons";
import { recordTrainingProfileQuestCompleted } from "../../../services/trainingProfilesService";
import {
  buildTrainingProfileConditionMetrics,
  doesTrainingProfileAnswerCheckMatch,
  evaluateConditionAst,
  getTrainingProfileStepAnswerChecks,
  hasTrainingProfileStepAnswerChecks,
} from "./trainingProfileUtils";
import styles from "./TutorialQuests.module.css";

const { Title, Paragraph, Text } = Typography;
const STEP_ANSWER_MAX_ATTEMPTS = 10;
const QUEST_PANEL_WIDTH = 520;
const QUEST_PANEL_MARGIN = 20;
const QUEST_SIDE_PANEL_GAP = 12;
const QUEST_PANEL_MIN_WIDTH = 520;
const QUEST_PANEL_MAX_WIDTH = 720;
const JUST_IN_TIME_PANEL_MIN_WIDTH = 320;
const JUST_IN_TIME_PANEL_MAX_WIDTH = 520;
const JUST_IN_TIME_PANEL_PREFERRED_WIDTH = 430;
const YES_NO_SELECT_VALUES = Object.freeze({
  yes: 'yes',
  no: 'no',
});

const createWidthPresets = (smallWidth, wideWidth) => Object.freeze({
  small: smallWidth,
  medium: Math.round((smallWidth + wideWidth) / 2),
  wide: wideWidth,
});

const QUEST_PANEL_WIDTH_PRESETS = createWidthPresets(QUEST_PANEL_WIDTH, QUEST_PANEL_MAX_WIDTH);
const JUST_IN_TIME_PANEL_WIDTH_PRESETS = createWidthPresets(JUST_IN_TIME_PANEL_PREFERRED_WIDTH, JUST_IN_TIME_PANEL_MAX_WIDTH);

const getBoundedPanelWidth = (preferredWidth, minWidth, maxWidth, availableWidth) => {
  if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
    return 0;
  }

  const boundedMax = Math.min(maxWidth, availableWidth);
  const boundedMin = Math.min(minWidth, boundedMax);
  return Math.min(Math.max(preferredWidth, boundedMin), boundedMax);
};

const getQuestPanelPreferredWidth = (viewportWidth, preferredWidth = QUEST_PANEL_WIDTH) => getBoundedPanelWidth(
  preferredWidth,
  QUEST_PANEL_MIN_WIDTH,
  QUEST_PANEL_MAX_WIDTH,
  viewportWidth - (QUEST_PANEL_MARGIN * 2)
);

const getQuestPanelMaxX = (viewportWidth, preferredWidth = QUEST_PANEL_WIDTH) => Math.max(
  QUEST_PANEL_MARGIN,
  viewportWidth - getQuestPanelPreferredWidth(viewportWidth, preferredWidth) - QUEST_PANEL_MARGIN
);

const getJustInTimePanelPreferredWidth = (
  viewportWidth,
  questPanelWidth,
  preferredWidth = JUST_IN_TIME_PANEL_PREFERRED_WIDTH,
) => getBoundedPanelWidth(
  preferredWidth,
  JUST_IN_TIME_PANEL_MIN_WIDTH,
  JUST_IN_TIME_PANEL_MAX_WIDTH,
  viewportWidth - (QUEST_PANEL_MARGIN * 2) - QUEST_SIDE_PANEL_GAP - questPanelWidth
);

const getQuestPanelMinX = (
  viewportWidth,
  questPreferredWidth = QUEST_PANEL_WIDTH,
  justInTimePreferredWidth = JUST_IN_TIME_PANEL_PREFERRED_WIDTH,
  hasJustInTimePanel = false,
) => {
  if (!hasJustInTimePanel) {
    return QUEST_PANEL_MARGIN;
  }

  const questWidth = getQuestPanelPreferredWidth(viewportWidth, questPreferredWidth);
  const justInTimeWidth = getJustInTimePanelPreferredWidth(viewportWidth, questWidth, justInTimePreferredWidth);
  return Math.min(
    getQuestPanelMaxX(viewportWidth, questPreferredWidth),
    QUEST_PANEL_MARGIN + justInTimeWidth + QUEST_SIDE_PANEL_GAP
  );
};

const QuestPanelWidthIcon = ({ preset }) => (
  <span className={styles.floatingPanelWidthIcon} aria-hidden="true">
    <span className={`${styles.floatingPanelWidthIconFrame} ${styles[`floatingPanelWidthIconFrame${preset}`]}`} />
  </span>
);

QuestPanelWidthIcon.propTypes = {
  preset: PropTypes.oneOf(['small', 'medium', 'wide']).isRequired,
};

const RichContent = ({ html, children }) => {
  if (!html) return children || null;
  return <div className={styles.richContentBlock} dangerouslySetInnerHTML={{ __html: html }} />;
};

RichContent.propTypes = {
  html: PropTypes.string,
  children: PropTypes.node,
};

const hasMeaningfulText = (value) => {
  if (typeof value !== 'string') return false;
  return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim().length > 0;
};

const hasRichContent = (value) => hasMeaningfulText(value);
const createDefaultStepAnswerCheckState = () => ({
  attempts: 0,
  solved: false,
  lastSubmissionCorrect: null,
});

const normalizeYesNoAnswerValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === YES_NO_SELECT_VALUES.yes) {
      return true;
    }
    if (normalized === YES_NO_SELECT_VALUES.no) {
      return false;
    }
  }

  return null;
};

const getYesNoSelectValue = (value) => {
  const normalized = normalizeYesNoAnswerValue(value);
  if (normalized === true) {
    return YES_NO_SELECT_VALUES.yes;
  }
  if (normalized === false) {
    return YES_NO_SELECT_VALUES.no;
  }
  return undefined;
};

const isAnswerCheckValueProvided = (answerCheck, learnerValue) => {
  if (!answerCheck || typeof answerCheck !== 'object') {
    return false;
  }

  if (answerCheck.type === 'yesNoQuestion') {
    return normalizeYesNoAnswerValue(learnerValue) !== null;
  }

  if (answerCheck.type === 'multipleChoice') {
    return normalizeMultipleChoiceSelection(learnerValue).length > 0;
  }

  return String(learnerValue ?? '').trim().length > 0;
};

const buildStepAnswerEntry = (entry, answerChecks) => {
  const answers = entry?.answers && typeof entry.answers === 'object' ? entry.answers : {};
  const rawCheckStates = entry?.checkStates && typeof entry.checkStates === 'object' ? entry.checkStates : {};
  const checks = Array.isArray(answerChecks) ? answerChecks : [];
  const checkStates = Object.fromEntries(
    checks.map((_, answerCheckIndex) => {
      const currentState = rawCheckStates[answerCheckIndex];
      return [
        answerCheckIndex,
        {
          attempts: Number.isFinite(currentState?.attempts) ? currentState.attempts : 0,
          solved: Boolean(currentState?.solved),
          lastSubmissionCorrect: currentState?.lastSubmissionCorrect ?? null,
        },
      ];
    })
  );
  const attempts = Object.values(checkStates).reduce((sum, currentState) => sum + currentState.attempts, 0);
  const answered = checks.length > 0 && checks.every((_, answerCheckIndex) => checkStates[answerCheckIndex]?.attempts > 0);
  const solved = checks.length > 0 && checks.every((_, answerCheckIndex) => Boolean(checkStates[answerCheckIndex]?.solved));
  const closed = checks.length > 0 && checks.every((_, answerCheckIndex) => {
    const currentState = checkStates[answerCheckIndex] || createDefaultStepAnswerCheckState();
    return currentState.solved || currentState.attempts >= STEP_ANSWER_MAX_ATTEMPTS;
  });

  return {
    answers,
    checkStates,
    attempts,
    answered,
    solved,
    closed,
    lastSubmissionCorrect: entry?.lastSubmissionCorrect ?? null,
  };
};

const getNormalizedLearnerValueForAnswerCheck = (answerCheck, learnerValue) => {
  if (answerCheck?.type === 'yesNoQuestion') {
    return normalizeYesNoAnswerValue(learnerValue);
  }

  if (answerCheck?.type === 'multipleChoice') {
    return normalizeMultipleChoiceSelection(learnerValue);
  }

  return learnerValue;
};

const normalizeMultipleChoiceSelection = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return [...new Set(values
    .map((entry) => {
      if (Number.isInteger(entry)) {
        return entry;
      }

      if (typeof entry === 'string' && entry.trim()) {
        const parsed = Number(entry);
        return Number.isInteger(parsed) ? parsed : null;
      }

      return null;
    })
    .filter((entry) => entry !== null))]
    .sort((left, right) => left - right);
};

const getMultipleChoiceCorrectOptionIndexes = (answerCheck) => {
  const indexes = normalizeMultipleChoiceSelection(answerCheck?.correctOptionIndexes);
  if (indexes.length > 0) {
    return indexes;
  }

  return normalizeMultipleChoiceSelection(answerCheck?.correctOptionIndex);
};

const getMultipleChoiceRevealText = (answerCheck) => {
  const options = Array.isArray(answerCheck?.options) ? answerCheck.options : [];
  const labels = getMultipleChoiceCorrectOptionIndexes(answerCheck)
    .map((optionIndex) => (options[optionIndex] ? `${optionIndex + 1}. ${options[optionIndex]}` : null))
    .filter(Boolean);

  return labels.join(', ');
};

const areConditionSnapshotsEqual = (left, right) => {
  if (left === right) {
    return true;
  }

  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const hasStepContent = (step) => {
  if (!step || typeof step !== 'object') return false;

  return [
    step.task,
    step.instruction,
    step.detailedGuidance,
    step.successIndicator,
    step.instructionRichContent,
    step.detailedGuidanceRichContent,
    step.successIndicatorRichContent,
    step.justInTimeRichContent,
    step.expectedValueName,
    ...(Array.isArray(step.answerChecks)
      ? step.answerChecks.flatMap((answerCheck) => [answerCheck?.name, answerCheck?.question, answerCheck?.justInTimeRichContent])
      : []),
  ].some(hasMeaningfulText);
};

const buildConditionLabel = (rule) => {
  if (!rule?.field || !rule?.operator) return 'Condition';
  if (rule.operator === 'between' && Array.isArray(rule.value)) {
    return `${rule.field} between ${rule.value[0]} and ${rule.value[1]}`;
  }
  if (rule.operator?.includes('FieldMultiplier')) {
    return `${rule.field} ${rule.operator} ${rule.compareField} × ${rule.multiplier ?? 1}`;
  }
  return `${rule.field} ${rule.operator} ${rule.value}`;
};

const getConditionLabels = (ast) => {
  if (!ast || typeof ast !== 'object') return [];
  if (Array.isArray(ast.rules)) return ast.rules.flatMap(getConditionLabels);
  return [ast.label || buildConditionLabel(ast)];
};

const normalizeProfileQuest = (quest, index) => ({
  id: quest.id,
  title: quest.title,
  category: quest.category || 'Training profile',
  difficulty: quest.difficulty || 'Beginner',
  estimatedTime: quest.estimatedTime,
  icon: renderQuestIcon(quest.iconKey),
  isChallenge: quest.isChallenge,
  conditionAst: quest.conditionAst,
  learningObjectivesRichContent: quest.learningObjectivesRichContent,
  scenarioRichContent: quest.scenarioRichContent,
  realWorldConnectionRichContent: quest.realWorldConnectionRichContent,
  completionDescriptionRichContent: quest.completionDescriptionRichContent,
  knowledgeSynthesisRichContent: quest.knowledgeSynthesisRichContent,
  practicalTipRichContent: quest.practicalTipRichContent,
  learningObjectives: [],
  scenario: { context: '' },
  completionCriteria: { conditions: getConditionLabels(quest.conditionAst) },
  knowledgeSynthesis: { keyTakeaways: [], formulaHighlight: '' },
  steps: (quest.steps || []).map((step, stepIndex) => ({
    id: step.stepKey || `profile-${quest.id}-${step.id || stepIndex}`,
    task: step.taskTitle,
    instruction: '',
    detailedGuidance: '',
    successIndicator: '',
    instructionRichContent: step.instructionRichContent,
    detailedGuidanceRichContent: step.detailedGuidanceRichContent,
    successIndicatorRichContent: step.successIndicatorRichContent,
    justInTimeRichContent: step.justInTimeRichContent,
    answerChecks: getTrainingProfileStepAnswerChecks(step),
    lockNextStepsUntilCorrect: Boolean(step.lockNextStepsUntilCorrect),
    spotlightTarget: step.spotlightTarget,
  })),
  displayIndex: index + 1,
});

const evaluateBuiltInQuestConditionStatus = (userType, metrics) => {
  const {
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    totalHeatLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    wallArea,
    uValue,
  } = metrics;

  const meetsHeatDemand = heatOutput >= totalHeatLoss * 0.9;
  const isLivingRoom = selectedRoomType === "living";
  const isTemperatureStable = Math.abs(currentTemperature - desiredTemperature) <= 2;
  const hasTargetWallArea = wallArea >= 48 && wallArea <= 52;
  const hasTargetWallUValue = uValue >= 0.18 && uValue <= 0.22;

  switch (userType) {
    case "energyConsultant":
      return {
        1: [isSimulationRunning],
        2: [outdoorTemperature <= -10, isLivingRoom, desiredTemperature === 20],
        3: [hasTargetWallArea, hasTargetWallUValue],
        4: [airChangeRate < 1.25],
        5: [supplyTemperature <= 40, meetsHeatDemand],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
    case "salesPerson":
      return {
        1: [isSimulationRunning],
        2: [isSimulationRunning],
        3: [isLivingRoom, desiredTemperature === 20],
        4: [hasTargetWallArea, hasTargetWallUValue],
        5: [supplyTemperature <= 40, meetsHeatDemand],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
    case "installer":
    default:
      return {
        1: [isSimulationRunning],
        2: [outdoorTemperature <= -10, isLivingRoom, desiredTemperature === 20],
        3: [supplyTemperature <= 45, meetsHeatDemand],
        4: [valvePosition <= 3 / 7, meetsHeatDemand],
        5: [airChangeRate < 1.25],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
  }
};



/**
 * Individual Quest Button Component
 * Wrapped with Ant Design Tooltip for better hover experience
 */
const QuestButton = ({ quest, isCompleted, isActive, onClick }) => {
  const { t } = useTranslation();
  const tooltipTitle = (
    <div>
      <div>{quest.title}</div>
      {isCompleted && (
        <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
          {`✓ ${t('heatPumpSimulation.tutorialQuests.completedStatus', 'Completed')}`}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip 
      title={tooltipTitle} 
      placement="top"
      color="white"
    >
      <div className={`${styles.questButtonWrapper} ${isCompleted ? styles.questButtonCompleted : ''}`}>
        <button
          className={`${styles.questButton} ${isActive ? styles.active : ""} ${quest.isChallenge ? styles.challenge : ""}`}
          onClick={onClick}
        >
          {quest.isChallenge ? (
            <TrophyOutlined className={styles.challengeIcon} />
          ) : (
            <span className={styles.questNumber}>{quest.displayIndex || quest.id}</span>
          )}
        </button>
      </div>
    </Tooltip>
  );
};

QuestButton.propTypes = {
  quest: PropTypes.object.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const InformationButton = ({ active, animate, onClick }) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t('heatPumpSimulation.informationTab', 'Information')}
      placement="top"
      color="white"
    >
      <div className={`${styles.questButtonWrapper} ${styles.informationButtonWrapper}`}>
        <button
          className={[
            styles.questButton,
            styles.informationButton,
            active ? styles.informationButtonActive : '',
            animate ? styles.informationButtonIntro : '',
          ].filter(Boolean).join(' ')}
          onClick={onClick}
        >
          <BulbOutlined className={styles.informationButtonIcon} />
        </button>
      </div>
    </Tooltip>
  );
};

InformationButton.propTypes = {
  active: PropTypes.bool,
  animate: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

/**
 * Difficulty Badge Component
 */
const DifficultyBadge = ({ difficulty }) => {
  const colorMap = {
    Beginner: "green",
    Anfänger: "green",
    Intermediate: "orange", 
    Mittelstufe: "orange",
    Advanced: "red",
    Fortgeschritten: "red",
    Expert: "purple",
    Experte: "purple"
  };
  return <Tag color={colorMap[difficulty] || "default"}>{difficulty}</Tag>;
};

/**
 * Tutorial Quests Container Component
 * 
 * Provides a progressive learning journey through heat pump system design
 * and optimization. Each quest builds upon previous knowledge.
 */
export const TutorialQuests = ({
  trainingProfile,
  trainingProfileMode,
  layoutMode = 'floating',
  controlsContent = null,
  dockedPanelTarget = null,
  // Simulation state for quest completion checking
  isSimulationRunning,
  outdoorTemperature,
  desiredTemperature,
  selectedRoomType,
  selectedCity,
  roofArea,
  wallArea,
  windowArea,
  uValue,
  totalArea,
  airChangeRate,
  supplyTemperature,
  heatOutput,
  envelopeLoss,
  ventilationLoss,
  valvePosition,
  thermostatLevel,
  currentCOP,
  currentTemperature,
  radiatorType,
  radiatorCount,
  systemPressure,
}) => {
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const profileMode = trainingProfileMode && !!trainingProfile;
  const isDockedLayout = layoutMode === 'docked';
  const hasProfileInformation = profileMode && hasRichContent(trainingProfile?.informationRichContent);
  const profileQuizAssignments = useMemo(
    () => (trainingProfile?.quizAssignments || []).filter(assignment => assignment.isQuizCollectionPublished !== false),
    [trainingProfile]
  );
  
  // User type state - determines which quest track to show
  const [userType, setUserType] = useState("installer");
  const [activeDockedTab, setActiveDockedTab] = useState(hasProfileInformation ? 'information' : 'controls');
  const [showInformationPulse, setShowInformationPulse] = useState(false);
  
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // Journey/Spotlight mode state
  const [isJourneyActive, setIsJourneyActive] = useState(false);
  const [journeyStepIndex, setJourneyStepIndex] = useState(0);
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  // Professional Quiz state
  const [showProfessionalQuiz, setShowProfessionalQuiz] = useState(false);
  const [showAssignedSkillQuiz, setShowAssignedSkillQuiz] = useState(false);
  const [stepAnswerState, setStepAnswerState] = useState({});
  const [storedQuestConditionStatus, setStoredQuestConditionStatus] = useState({});
  const [storedQuestCompletionStatus, setStoredQuestCompletionStatus] = useState({});
  const [completionCelebrationQuestId, setCompletionCelebrationQuestId] = useState(null);
  const [expandedStepKeys, setExpandedStepKeys] = useState([]);
  const [activeQuestSectionId, setActiveQuestSectionId] = useState(null);
  const [justInTimePanel, setJustInTimePanel] = useState(null);
  const [questPanelWidthPreset, setQuestPanelWidthPreset] = useState('small');
  const [justInTimePanelWidthPreset, setJustInTimePanelWidthPreset] = useState('small');
  const [panelSize, setPanelSize] = useState({ width: QUEST_PANEL_WIDTH, height: null });
  const questPanelPreferredWidth = QUEST_PANEL_WIDTH_PRESETS[questPanelWidthPreset] || QUEST_PANEL_WIDTH;
  const justInTimePanelPreferredWidth = JUST_IN_TIME_PANEL_WIDTH_PRESETS[justInTimePanelWidthPreset] || JUST_IN_TIME_PANEL_PREFERRED_WIDTH;
  // Dragging state - calculate right-side position with mobile-safe fallback
  const getInitialPosition = useCallback(() => {
    const x = getQuestPanelMaxX(window.innerWidth, questPanelPreferredWidth);
    return { x, y: 80 };
  }, [questPanelPreferredWidth]);
  const [position, setPosition] = useState(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const questScrollContainerRef = useRef(null);
  const questSectionRefs = useRef({});
  const dragStartPos = useRef({ x: 0, y: 0 });
  const hasShownInformationPulseRef = useRef(false);
  const celebratedQuestIdsRef = useRef(new Set());
  const celebrationTimeoutRef = useRef(null);

  useEffect(() => {
    if (!hasProfileInformation || hasShownInformationPulseRef.current) {
      return undefined;
    }

    hasShownInformationPulseRef.current = true;
    setShowInformationPulse(true);
    const timeoutId = window.setTimeout(() => {
      setShowInformationPulse(false);
    }, 2700);

    return () => window.clearTimeout(timeoutId);
  }, [hasProfileInformation]);

  useEffect(() => () => {
    if (celebrationTimeoutRef.current) {
      window.clearTimeout(celebrationTimeoutRef.current);
    }
  }, []);

  // Update position on window resize to keep panel visible
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const minX = getQuestPanelMinX(
          window.innerWidth,
          questPanelPreferredWidth,
          justInTimePanelPreferredWidth,
          Boolean(justInTimePanel)
        );
        const maxX = getQuestPanelMaxX(window.innerWidth, questPanelPreferredWidth);
        return { x: Math.max(minX, Math.min(prev.x, maxX)), y: prev.y };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [justInTimePanel, justInTimePanelPreferredWidth, questPanelPreferredWidth]);

  useEffect(() => {
    if (!panelVisible || isMinimized || !dragRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updatePanelSize = () => {
      const nextRect = dragRef.current?.getBoundingClientRect();
      if (!nextRect) {
        return;
      }

      setPanelSize((previous) => {
        const nextWidth = Math.round(nextRect.width) || QUEST_PANEL_WIDTH;
        const nextHeight = Math.round(nextRect.height) || null;
        if (previous.width === nextWidth && previous.height === nextHeight) {
          return previous;
        }

        return {
          width: nextWidth,
          height: nextHeight,
        };
      });
    };

    updatePanelSize();

    const resizeObserver = new ResizeObserver(() => {
      updatePanelSize();
    });
    resizeObserver.observe(dragRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMinimized, panelVisible, selectedQuest]);

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : QUEST_PANEL_MAX_WIDTH + (QUEST_PANEL_MARGIN * 2);
  const floatingPanelWidth = useMemo(
    () => getQuestPanelPreferredWidth(viewportWidth, questPanelPreferredWidth),
    [questPanelPreferredWidth, viewportWidth]
  );
  const floatingJustInTimeWidth = useMemo(() => {
    if (!justInTimePanel) {
      return 0;
    }

    return getJustInTimePanelPreferredWidth(viewportWidth, floatingPanelWidth, justInTimePanelPreferredWidth);
  }, [floatingPanelWidth, justInTimePanel, justInTimePanelPreferredWidth, viewportWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const minX = getQuestPanelMinX(
      window.innerWidth,
      questPanelPreferredWidth,
      justInTimePanelPreferredWidth,
      Boolean(justInTimePanel)
    );
    const maxX = getQuestPanelMaxX(window.innerWidth, questPanelPreferredWidth);
    setPosition((previous) => (
      previous.x >= minX && previous.x <= maxX
        ? previous
        : { ...previous, x: Math.max(minX, Math.min(previous.x, maxX)) }
    ));
  }, [justInTimePanel, justInTimePanelPreferredWidth, questPanelPreferredWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setPosition((previous) => {
      const minX = getQuestPanelMinX(
        window.innerWidth,
        questPanelPreferredWidth,
        justInTimePanelPreferredWidth,
        Boolean(justInTimePanel)
      );
      const maxX = getQuestPanelMaxX(window.innerWidth, questPanelPreferredWidth);
      if (previous.x >= minX && previous.x <= maxX) {
        return previous;
      }

      return {
        ...previous,
        x: Math.max(minX, Math.min(previous.x, maxX)),
      };
    });
  }, [justInTimePanel, justInTimePanelPreferredWidth, questPanelPreferredWidth]);

  // Get quests for the current user type
  const QUESTS = useMemo(() => {
    if (profileMode) {
      return (trainingProfile?.tutorialQuests || []).map(normalizeProfileQuest);
    }

    return getQuestsForUserType(userType, activeLanguage);
  }, [activeLanguage, profileMode, trainingProfile, userType]);

  useEffect(() => {
    if (!isDockedLayout) {
      return;
    }

    setActiveDockedTab(hasProfileInformation ? 'information' : 'controls');
  }, [hasProfileInformation, isDockedLayout, trainingProfile?.id]);

  useEffect(() => {
    setStepAnswerState({});
    setStoredQuestConditionStatus({});
    setStoredQuestCompletionStatus({});
  }, [profileMode, trainingProfile?.id, userType]);

  useEffect(() => {
    if (!selectedQuest) {
      return;
    }

    const localizedQuest = QUESTS.find((quest) => quest.id === selectedQuest.id);

    if (!localizedQuest) {
      setSelectedQuest(null);
      return;
    }

    if (localizedQuest !== selectedQuest) {
      setSelectedQuest(localizedQuest);
    }
  }, [QUESTS, selectedQuest]);

  const simulationMetrics = useMemo(() => buildTrainingProfileConditionMetrics({
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    envelopeLoss,
    ventilationLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    thermostatLevel,
    radiatorType,
    radiatorCount,
    systemPressure,
    roofArea,
    wallArea,
    windowArea,
    uValue,
    totalArea,
    selectedCity,
  }), [
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    envelopeLoss,
    ventilationLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    thermostatLevel,
    radiatorType,
    radiatorCount,
    systemPressure,
    roofArea,
    wallArea,
    windowArea,
    uValue,
    totalArea,
    selectedCity,
  ]);

  const getStepAnswerEntry = useCallback((stepId, answerChecks = []) => {
    return buildStepAnswerEntry(stepAnswerState[stepId], answerChecks);
  }, [stepAnswerState]);

  const evaluateQuestConditionSnapshot = useCallback((quest) => {
    if (!quest) {
      return [];
    }

    const hasConditionAst = quest.conditionAst !== null && typeof quest.conditionAst === 'object';
    const stepsWithAnswerChecks = Array.isArray(quest.steps)
      ? quest.steps.filter((step) => hasTrainingProfileStepAnswerChecks(step))
      : [];
    const answerCheckCompletionSnapshot = stepsWithAnswerChecks.length > 0
      ? [stepsWithAnswerChecks.every((step) => {
          const answerChecks = getTrainingProfileStepAnswerChecks(step);
          return getStepAnswerEntry(step.id, answerChecks).closed;
        })]
      : [];

    if (profileMode) {
      const astSnapshot = hasConditionAst
        ? (() => {
            const evaluation = evaluateConditionAst(quest.conditionAst, simulationMetrics);
            return evaluation.results.length > 0
              ? evaluation.results.map((result) => result.passed)
              : [evaluation.passed];
          })()
        : [];
      return [...astSnapshot, ...answerCheckCompletionSnapshot];
    }

    const builtInStatus = evaluateBuiltInQuestConditionStatus(userType, simulationMetrics);
    return [...(builtInStatus[quest.id] || []), ...answerCheckCompletionSnapshot];
  }, [getStepAnswerEntry, profileMode, simulationMetrics, userType]);

  useEffect(() => {
    if (!panelVisible || !selectedQuest) {
      return;
    }

    const conditionSnapshot = evaluateQuestConditionSnapshot(selectedQuest);
    const nextCompleted = conditionSnapshot.length > 0 && conditionSnapshot.every(Boolean);

    setStoredQuestConditionStatus((previous) => {
      const currentSnapshot = previous[selectedQuest.id] || [];
      if (areConditionSnapshotsEqual(currentSnapshot, conditionSnapshot)) {
        return previous;
      }

      return {
        ...previous,
        [selectedQuest.id]: conditionSnapshot,
      };
    });

    if (nextCompleted) {
      setStoredQuestCompletionStatus((previous) => {
        if (previous[selectedQuest.id]) {
          return previous;
        }

        return {
          ...previous,
          [selectedQuest.id]: true,
        };
      });
    }
  }, [evaluateQuestConditionSnapshot, panelVisible, selectedQuest]);

  // Count completed quests
  const completedCount = useMemo(() => {
    return QUESTS.filter((quest) => storedQuestCompletionStatus[quest.id]).length;
  }, [QUESTS, storedQuestCompletionStatus]);

  // Report quest completions to the analytics endpoint exactly once per quest per profile session.
  // Only DB-driven profile quests are recorded; static quests have no profile context to attach to.
  const reportedQuestCompletionsRef = useRef(new Set());

  useEffect(() => {
    reportedQuestCompletionsRef.current = new Set();
  }, [profileMode, trainingProfile?.id, userType]);

  useEffect(() => {
    if (!profileMode || !trainingProfile?.id) {
      return;
    }

    Object.entries(storedQuestCompletionStatus).forEach(([questId, completed]) => {
      if (!completed || reportedQuestCompletionsRef.current.has(questId)) {
        return;
      }
      reportedQuestCompletionsRef.current.add(questId);

      const quest = QUESTS.find((candidate) => String(candidate.id) === String(questId));
      const numericQuestId = Number(questId);
      recordTrainingProfileQuestCompleted(trainingProfile.id, {
        questId: Number.isInteger(numericQuestId) ? numericQuestId : null,
        questKey: String(questId),
        questTitle: quest?.title || null,
      }, { suppressNotification: true }).catch(() => {});
    });
  }, [QUESTS, profileMode, storedQuestCompletionStatus, trainingProfile?.id]);

  // Find the first incomplete quest (for "active" highlighting)
  const activeQuestId = useMemo(() => {
    return QUESTS.find((quest) => !storedQuestCompletionStatus[quest.id])?.id || null;
  }, [QUESTS, storedQuestCompletionStatus]);

  // Dragging handlers
  const handleDragStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  }, [position]);

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;
      const minX = getQuestPanelMinX(
        window.innerWidth,
        questPanelPreferredWidth,
        justInTimePanelPreferredWidth,
        Boolean(justInTimePanel)
      );
      const maxX = getQuestPanelMaxX(window.innerWidth, questPanelPreferredWidth);
      setPosition({
        x: Math.max(minX, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 100))
      });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, justInTimePanel, justInTimePanelPreferredWidth, questPanelPreferredWidth]);

  const handleQuestClick = useCallback((quest) => {
    const alreadyCompleted = Boolean(storedQuestCompletionStatus[quest.id]);

    if (!alreadyCompleted) {
      const conditionSnapshot = evaluateQuestConditionSnapshot(quest);
      const nextCompleted = conditionSnapshot.length > 0 && conditionSnapshot.every(Boolean);

      setStoredQuestConditionStatus((previous) => ({
        ...previous,
        [quest.id]: conditionSnapshot,
      }));
      setStoredQuestCompletionStatus((previous) => ({
        ...previous,
        [quest.id]: previous[quest.id] || nextCompleted,
      }));
    }

    setSelectedQuest(quest);
    setJourneyStepIndex(0);
    setExpandedStepKeys([]);
    setShowQuiz(false);
    setShowCongrats(false);
    setPanelVisible(true);
    setIsMinimized(false);
    if (isDockedLayout) {
      setActiveDockedTab('quest');
    }
  }, [evaluateQuestConditionSnapshot, isDockedLayout, storedQuestCompletionStatus]);

  const handleClosePanel = useCallback(() => {
    setPanelVisible(false);
    setSelectedQuest(null);
    setExpandedStepKeys([]);
    setShowQuiz(false);
    setShowCongrats(false);
    setJustInTimePanel(null);
    if (isDockedLayout) {
      setActiveDockedTab(hasProfileInformation ? 'information' : 'controls');
    }
  }, [hasProfileInformation, isDockedLayout]);

  const handleInformationClick = useCallback(() => {
    if (!hasProfileInformation) {
      return;
    }

    setJustInTimePanel(null);
    setActiveDockedTab('information');
  }, [hasProfileInformation]);

  const handleDockedTabChange = useCallback((nextKey) => {
    setActiveDockedTab(nextKey);
    if (nextKey !== 'quest') {
      setJustInTimePanel(null);
    }
  }, []);

  useEffect(() => {
    if (!panelVisible || isMinimized || !selectedQuest) {
      setJustInTimePanel(null);
    }
  }, [isMinimized, panelVisible, selectedQuest]);

  const toggleJustInTimePanel = useCallback((nextPanel) => {
    if (!nextPanel?.id || !hasRichContent(nextPanel.html)) {
      return;
    }

    setJustInTimePanel((previous) => previous?.id === nextPanel.id ? null : nextPanel);
  }, []);

  const handleCloseJustInTimePanel = useCallback(() => {
    setJustInTimePanel(null);
  }, []);

  const updateStepAnswerValue = useCallback((stepId, answerCheckIndex, value) => {
    setStepAnswerState((previous) => {
      const current = previous[stepId] ?? {
        answers: {},
        lastSubmissionCorrect: null,
        checkStates: {},
      };

      return {
        ...previous,
        [stepId]: {
          ...current,
          answers: {
            ...(current.answers && typeof current.answers === 'object' ? current.answers : {}),
            [answerCheckIndex]: value,
          },
        },
      };
    });
  }, []);

  const handleCheckSingleStepAnswer = useCallback((step, answerCheckIndex) => {
    const answerChecks = getTrainingProfileStepAnswerChecks(step);
    const answerCheck = answerChecks[answerCheckIndex];

    if (!answerCheck) {
      return;
    }

    setStepAnswerState((previous) => {
      const current = buildStepAnswerEntry(previous[step.id], answerChecks);
      const currentCheckState = current.checkStates[answerCheckIndex] || createDefaultStepAnswerCheckState();

      if (currentCheckState.solved || currentCheckState.attempts >= STEP_ANSWER_MAX_ATTEMPTS) {
        return previous;
      }

      const learnerValue = current.answers[answerCheckIndex];
      if (!isAnswerCheckValueProvided(answerCheck, learnerValue)) {
        return previous;
      }

      const isCorrect = doesTrainingProfileAnswerCheckMatch(
        answerCheck,
        getNormalizedLearnerValueForAnswerCheck(answerCheck, learnerValue)
      );
      const nextCheckStates = {
        ...current.checkStates,
        [answerCheckIndex]: {
          attempts: currentCheckState.attempts + 1,
          solved: isCorrect,
          lastSubmissionCorrect: isCorrect,
        },
      };
      const nextEntry = buildStepAnswerEntry({
        ...previous[step.id],
        answers: current.answers,
        checkStates: nextCheckStates,
        lastSubmissionCorrect: isCorrect,
      }, answerChecks);

      return {
        ...previous,
        [step.id]: {
          ...previous[step.id],
          answers: nextEntry.answers,
          checkStates: nextEntry.checkStates,
          attempts: nextEntry.attempts,
          answered: nextEntry.answered,
          solved: nextEntry.solved,
          closed: nextEntry.closed,
          lastSubmissionCorrect: isCorrect,
        },
      };
    });
  }, []);

  // Journey/Tour handlers
  const handleStartJourney = useCallback(() => {
    setJourneyStepIndex(0);
    setIsJourneyActive(true);
    setIsMinimized(true);
  }, []);

  const handleJourneyNext = useCallback(() => {
    if (selectedQuest && journeyStepIndex < selectedQuest.steps.length - 1) {
      setJourneyStepIndex((prev) => prev + 1);
    }
  }, [selectedQuest, journeyStepIndex]);

  const handleJourneyPrevious = useCallback(() => {
    if (journeyStepIndex > 0) {
      setJourneyStepIndex((prev) => prev - 1);
    }
  }, [journeyStepIndex]);

  const handleJourneyClose = useCallback(() => {
    setIsJourneyActive(false);
    setIsMinimized(false);
  }, []);

  const handleJourneyComplete = useCallback(() => {
    setIsJourneyActive(false);
    setIsMinimized(false);
    // Show congratulations and prompt for quiz
    if (selectedQuest && storedQuestCompletionStatus[selectedQuest.id]) {
      setShowCongrats(true);
    }
  }, [selectedQuest, storedQuestCompletionStatus]);

  // Quiz handlers
  const handleStartQuiz = useCallback(() => {
    if (profileMode) return;
    setShowCongrats(false);
    setShowQuiz(true);
  }, [profileMode]);

  const handleCloseQuiz = useCallback(() => {
    setShowQuiz(false);
  }, []);

  const handleBackToQuest = useCallback(() => {
    setShowQuiz(false);
    setShowCongrats(false);
  }, []);

  const handleResetQuestProgress = useCallback((questId) => {
    const quest = QUESTS.find((entry) => entry.id === questId);
    const stepIds = new Set((quest?.steps || []).map((step) => String(step.id)));

    setStoredQuestCompletionStatus((previous) => {
      const next = { ...previous };
      delete next[questId];
      return next;
    });
    setStoredQuestConditionStatus((previous) => {
      const next = { ...previous };
      delete next[questId];
      return next;
    });
    setStepAnswerState((previous) => Object.fromEntries(
      Object.entries(previous).filter(([stepId]) => !stepIds.has(stepId))
    ));
    setJourneyStepIndex(0);
    setExpandedStepKeys([]);
    setShowCongrats(false);
    setShowQuiz(false);
    setIsJourneyActive(false);
    setIsMinimized(false);
    celebratedQuestIdsRef.current.delete(String(questId));
    if (String(completionCelebrationQuestId) === String(questId)) {
      setCompletionCelebrationQuestId(null);
    }
  }, [QUESTS, completionCelebrationQuestId]);

  const isQuestCompleted = useCallback((questId) => Boolean(storedQuestCompletionStatus[questId]), [storedQuestCompletionStatus]);
  const selectedQuestCompleted = Boolean(selectedQuest && isQuestCompleted(selectedQuest.id));
  const showCompletionCelebration = Boolean(
    completionCelebrationQuestId
    && selectedQuest
    && String(selectedQuest.id) === String(completionCelebrationQuestId)
  );

  // Calculate progress percentage
  const progressPercent = QUESTS.length > 0 ? Math.round((completedCount / QUESTS.length) * 100) : 0;

  useEffect(() => {
    const selectedQuestId = selectedQuest?.id;
    if (!selectedQuestId || !selectedQuestCompleted) {
      return;
    }

    const questKey = String(selectedQuestId);
    if (celebratedQuestIdsRef.current.has(questKey)) {
      return;
    }

    celebratedQuestIdsRef.current.add(questKey);
    setCompletionCelebrationQuestId(questKey);
    if (celebrationTimeoutRef.current) {
      window.clearTimeout(celebrationTimeoutRef.current);
    }
    celebrationTimeoutRef.current = window.setTimeout(() => {
      setCompletionCelebrationQuestId(null);
    }, 3600);
  }, [selectedQuest, selectedQuestCompleted]);

  // Handle user type change
  const handleUserTypeChange = useCallback((value) => {
    setUserType(value);
    // Reset selected quest when switching user types
    setSelectedQuest(null);
    setPanelVisible(false);
    setShowQuiz(false);
    setShowCongrats(false);
    setJustInTimePanel(null);
  }, []);

  const handleProfileSkillQuizClick = useCallback(() => {
    setShowAssignedSkillQuiz(true);
  }, []);

  const selectedQuestIndex = selectedQuest ? QUESTS.findIndex((quest) => String(quest.id) === String(selectedQuest.id)) : -1;
  const nextQuest = selectedQuestIndex >= 0 ? QUESTS[selectedQuestIndex + 1] || null : null;

  const handleOpenNextQuest = useCallback(() => {
    if (!nextQuest) {
      return;
    }

    handleQuestClick(nextQuest);
  }, [handleQuestClick, nextQuest]);

  const learningObjectives = selectedQuest?.learningObjectives || [];
  const completionConditions = selectedQuest?.completionCriteria?.conditions?.filter(hasMeaningfulText) || [];
  const hasQuestAnswerCheckCompletionCondition = Array.isArray(selectedQuest?.steps)
    && selectedQuest.steps.some((step) => hasTrainingProfileStepAnswerChecks(step));
  const completionConditionItems = [
    ...completionConditions.map((condition) => ({ type: 'ast', label: condition })),
    ...(hasQuestAnswerCheckCompletionCondition
      ? [{
          type: 'answerChecks',
          label: t('heatPumpSimulation.tutorialQuests.answerChecksCompletionCondition', 'All step answer checks answered'),
        }]
      : []),
  ];
  const allVisibleCandidateSteps = useMemo(
    () => (selectedQuest?.steps || []).filter(hasStepContent),
    [selectedQuest]
  );
  const firstBlockingStepIndex = useMemo(() => {
    if (!profileMode) {
      return -1;
    }

    return allVisibleCandidateSteps.findIndex((step) => {
      if (!step.lockNextStepsUntilCorrect || !hasTrainingProfileStepAnswerChecks(step)) {
        return false;
      }

      const answerChecks = getTrainingProfileStepAnswerChecks(step);
      const answerEntry = getStepAnswerEntry(step.id, answerChecks);
      return !answerEntry.closed;
    });
  }, [allVisibleCandidateSteps, getStepAnswerEntry, profileMode]);
  const visibleSteps = allVisibleCandidateSteps;
  const lockedStepCount = firstBlockingStepIndex === -1
    ? 0
    : Math.max(allVisibleCandidateSteps.length - firstBlockingStepIndex - 1, 0);
  const lockedStepIds = useMemo(
    () => new Set(
      firstBlockingStepIndex === -1
        ? []
        : allVisibleCandidateSteps
          .slice(firstBlockingStepIndex + 1)
          .map((step) => String(step.id))
    ),
    [allVisibleCandidateSteps, firstBlockingStepIndex]
  );
  const hasLearningObjectivesSection = hasRichContent(selectedQuest?.learningObjectivesRichContent)
    || learningObjectives.some(hasMeaningfulText);
  const hasScenarioSection = hasRichContent(selectedQuest?.scenarioRichContent)
    || hasMeaningfulText(selectedQuest?.scenario?.context);
  const hasStepsSection = visibleSteps.length > 0;
  const hasCompletionSection = completionConditionItems.length > 0;
  const hasKnowledgeSynthesisSection = Boolean(selectedQuest)
    && isQuestCompleted(selectedQuest.id)
    && (
      hasRichContent(selectedQuest?.knowledgeSynthesisRichContent)
      || (selectedQuest?.knowledgeSynthesis?.keyTakeaways || []).some(hasMeaningfulText)
      || hasMeaningfulText(selectedQuest?.knowledgeSynthesis?.formulaHighlight)
    );
  const questContentSectionItems = useMemo(() => {
    const items = [];

    if (hasLearningObjectivesSection) {
      items.push({ id: 'learning-objectives', label: t('trainingProfiles.learningObjectives', 'Learning objectives') });
    }

    if (hasScenarioSection) {
      items.push({ id: 'scenario', label: t('trainingProfiles.scenario', 'Scenario') });
    }

    if (hasStepsSection) {
      items.push({ id: 'steps', label: t('trainingProfiles.stepsTitle', 'Steps') });
    }

    if (hasCompletionSection) {
      items.push({ id: 'completion', label: t('heatPumpSimulation.tutorialQuests.completion', 'Completion') });
    }

    if (hasKnowledgeSynthesisSection) {
      items.push({ id: 'key-takeaways', label: t('heatPumpSimulation.tutorialQuests.keyTakeaways', 'Key takeaways') });
    }

    return items;
  }, [
    hasCompletionSection,
    hasKnowledgeSynthesisSection,
    hasLearningObjectivesSection,
    hasScenarioSection,
    hasStepsSection,
    t,
  ]);
  const shouldShowQuestSectionNav = Boolean(selectedQuest) && questContentSectionItems.length >= 3;
  const hasAnyQuests = QUESTS.length > 0;

  const setQuestSectionRef = useCallback((sectionId, node) => {
    if (node) {
      questSectionRefs.current[sectionId] = node;
      return;
    }

    delete questSectionRefs.current[sectionId];
  }, []);

  const getQuestScrollContainer = useCallback(() => {
    let node = questScrollContainerRef.current;

    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return node;
      }
      node = node.parentElement;
    }

    return questScrollContainerRef.current;
  }, []);

  const getQuestSectionScrollTop = useCallback((sectionNode, scrollContainer) => {
    if (!sectionNode || !scrollContainer) {
      return 0;
    }

    return sectionNode.getBoundingClientRect().top
      - scrollContainer.getBoundingClientRect().top
      + scrollContainer.scrollTop;
  }, []);

  const updateActiveQuestSection = useCallback(() => {
    if (!shouldShowQuestSectionNav || questContentSectionItems.length === 0) {
      setActiveQuestSectionId(null);
      return;
    }

    const scrollContainer = getQuestScrollContainer();
    if (!scrollContainer) {
      return;
    }

    const threshold = scrollContainer.scrollTop + 72;
    const positionedSections = questContentSectionItems
      .map((sectionItem) => {
        const sectionNode = questSectionRefs.current[sectionItem.id];
        if (!sectionNode) {
          return null;
        }

        return {
          id: sectionItem.id,
          top: getQuestSectionScrollTop(sectionNode, scrollContainer),
        };
      })
      .filter(Boolean);

    if (positionedSections.length === 0) {
      return;
    }

    let nextActiveSectionId = positionedSections[0].id;

    for (let index = 0; index < positionedSections.length; index += 1) {
      const currentSection = positionedSections[index];
      const nextSection = positionedSections[index + 1];

      if (!nextSection || threshold < nextSection.top) {
        nextActiveSectionId = currentSection.id;
        break;
      }
    }

    setActiveQuestSectionId((previous) => (previous === nextActiveSectionId ? previous : nextActiveSectionId));
  }, [getQuestScrollContainer, getQuestSectionScrollTop, questContentSectionItems, shouldShowQuestSectionNav]);

  const handleQuestSectionNavClick = useCallback((sectionId) => {
    const scrollContainer = getQuestScrollContainer();
    const sectionNode = questSectionRefs.current[sectionId];

    if (!scrollContainer || !sectionNode) {
      return;
    }

    scrollContainer.scrollTo({
      top: Math.max(getQuestSectionScrollTop(sectionNode, scrollContainer) - 8, 0),
      behavior: 'smooth',
    });
    setActiveQuestSectionId(sectionId);
  }, [getQuestScrollContainer, getQuestSectionScrollTop]);

  useEffect(() => {
    questSectionRefs.current = {};
    setActiveQuestSectionId(null);
  }, [selectedQuest?.id]);

  useEffect(() => {
    if (!shouldShowQuestSectionNav) {
      setActiveQuestSectionId(null);
      return undefined;
    }

    const scrollContainer = getQuestScrollContainer();
    if (!scrollContainer) {
      return undefined;
    }

    const handleScrollOrResize = () => {
      updateActiveQuestSection();
    };

    handleScrollOrResize();
    scrollContainer.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [getQuestScrollContainer, shouldShowQuestSectionNav, updateActiveQuestSection, selectedQuest?.id]);

  useEffect(() => {
    setExpandedStepKeys((previous) => previous.filter((key) => !lockedStepIds.has(String(key))));
  }, [lockedStepIds]);

  const handleStepCollapseChange = useCallback((keys) => {
    const nextKeys = Array.isArray(keys)
      ? keys
      : keys
        ? [keys]
        : [];

    setExpandedStepKeys(nextKeys.filter((key) => !lockedStepIds.has(String(key))));
  }, [lockedStepIds]);

  const renderJustInTimeTrigger = useCallback((panelConfig, tooltipText) => {
    if (!panelConfig || !hasRichContent(panelConfig.html)) {
      return null;
    }

    const isActive = justInTimePanel?.id === panelConfig.id;

    return (
      <Tooltip title={tooltipText} color="white">
        <Button
          type="text"
          size="small"
          icon={<QuestionCircleOutlined />}
          className={`${styles.justInTimeTrigger}${isActive ? ` ${styles.justInTimeTriggerActive}` : ''}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleJustInTimePanel(panelConfig);
          }}
        />
      </Tooltip>
    );
  }, [justInTimePanel?.id, toggleJustInTimePanel]);

  const justInTimePanelLeft = Math.max(QUEST_PANEL_MARGIN, position.x - floatingJustInTimeWidth - QUEST_SIDE_PANEL_GAP);
  const widthPresetOptions = [
    { key: 'small', tooltip: t('heatPumpSimulation.tutorialQuests.widthSmall', 'Small width') },
    { key: 'medium', tooltip: t('heatPumpSimulation.tutorialQuests.widthMedium', 'Medium width') },
    { key: 'wide', tooltip: t('heatPumpSimulation.tutorialQuests.widthWide', 'Wide width') },
  ];

  // User type options for the dropdown
  const userTypeOptions = [
    { value: 'installer', label: t('heatPumpSimulation.tutorialQuests.userTypes.installer'), icon: <ToolOutlined /> },
    { value: 'energyConsultant', label: t('heatPumpSimulation.tutorialQuests.userTypes.energyConsultant'), icon: <CalculatorOutlined /> },
    { value: 'salesPerson', label: t('heatPumpSimulation.tutorialQuests.userTypes.salesPerson'), icon: <TeamOutlined /> }
  ];

  const renderSelectedQuestHeader = ({ docked = false } = {}) => {
    if (!selectedQuest) {
      return null;
    }

    return (
      <div
        className={docked ? styles.dockedQuestHeader : styles.floatingPanelHeader}
        onMouseDown={!docked ? handleDragStart : undefined}
        style={!docked ? { cursor: isDragging ? 'grabbing' : 'grab' } : undefined}
      >
        <div className={styles.floatingPanelHeaderMain}>
          {selectedQuest?.icon ? (
            <span style={{ fontSize: 18, color: "#1890ff" }}>{selectedQuest.icon}</span>
          ) : (
            <span className={styles.modalQuestNumber}>{selectedQuest?.id}</span>
          )}
          <div className={styles.floatingPanelHeaderText}>
            <div className={styles.floatingPanelHeaderTitle}>{selectedQuest?.title}</div>
            <Space size="small" className={styles.floatingPanelHeaderMeta}>
              <Tag color="blue" style={{ fontSize: 10, padding: '0 4px' }}>{selectedQuest?.category}</Tag>
              {selectedQuest && <DifficultyBadge difficulty={selectedQuest.difficulty} />}
            </Space>
          </div>
        </div>
        <div className={styles.floatingPanelHeaderControls}>
          {!docked && (
            <Space size={4} className={styles.floatingPanelWidthControls}>
              {widthPresetOptions.map((option) => (
                <Tooltip key={option.key} title={option.tooltip} color="white">
                  <Button
                    type="text"
                    size="small"
                    icon={<QuestPanelWidthIcon preset={option.key} />}
                    className={`${styles.floatingPanelHeaderIconButton}${questPanelWidthPreset === option.key ? ` ${styles.floatingPanelHeaderIconButtonActive}` : ''}`}
                    onClick={() => setQuestPanelWidthPreset(option.key)}
                  />
                </Tooltip>
              ))}
            </Space>
          )}
          <Space size={4}>
            {!docked && (
              <Tooltip title={t('heatPumpSimulation.tutorialQuests.minimize', 'Minimize')} color="white">
                <Button
                  type="text"
                  size="small"
                  icon={<MinusOutlined />}
                  onClick={() => setIsMinimized(true)}
                />
              </Tooltip>
            )}
            <Tooltip title={t('heatPumpSimulation.tutorialQuests.close', 'Close')} color="white">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={handleClosePanel}
              />
            </Tooltip>
          </Space>
        </div>
      </div>
    );
  };

  const renderSelectedQuestPanelBody = () => {
    if (!selectedQuest) {
      return null;
    }

    return showQuiz ? (
      <TutorialQuiz
        questId={selectedQuest.id}
        questTitle={selectedQuest.title}
        userType={userType}
        isOpen={showQuiz}
        onClose={handleCloseQuiz}
        onBackToQuest={handleBackToQuest}
      />
    ) : showCongrats ? (
      <div className={styles.congratsContainer}>
        <div className={styles.congratsSvg}>
          <svg width="100" height="100" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="celebrateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#52c41a" />
                <stop offset="100%" stopColor="#73d13d" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#celebrateGrad)" />
            <path
              d="M30 50 L45 65 L70 35"
              stroke="#fff"
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <circle cx="20" cy="20" r="4" fill="#ffd700" className={styles.sparkle1} />
            <circle cx="80" cy="25" r="3" fill="#ffd700" className={styles.sparkle2} />
            <circle cx="85" cy="70" r="3" fill="#ffd700" className={styles.sparkle3} />
            <circle cx="15" cy="75" r="3" fill="#ffd700" className={styles.sparkle2} />
          </svg>
        </div>
        <Title level={4} style={{ color: '#52c41a', marginTop: 16 }}>
          {t('heatPumpSimulation.tutorialQuests.questCompletedTitle', 'Quest completed!')}
        </Title>
        <Paragraph style={{ color: '#595959' }}>
          {t('heatPumpSimulation.tutorialQuests.questCompletedMessage', 'Excellent work! You completed "{{title}}".', {
            title: selectedQuest.title,
          })}
          {!profileMode && <><br />{t('heatPumpSimulation.tutorialQuests.readyToTestKnowledge', 'Ready to test your knowledge?')}</>}
        </Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          {!profileMode && (
            <Button
              type="primary"
              size="large"
              icon={<QuestionCircleOutlined />}
              onClick={handleStartQuiz}
              block
            >
              {t('heatPumpSimulation.tutorialQuests.takeQuiz', 'Take the quiz')}
            </Button>
          )}
          <Button onClick={handleBackToQuest} block>
            {t('heatPumpSimulation.tutorialQuests.reviewQuestDetails', 'Review quest details')}
          </Button>
        </Space>
      </div>
    ) : (
      <div className={`${styles.questDetailsContent}${selectedQuestCompleted ? ` ${styles.questDetailsContentCompleted}` : ''}${shouldShowQuestSectionNav ? ` ${styles.questDetailsContentWithSectionNav}` : ''}`}>
        <div className={styles.questDetailsMain}>
        {selectedQuestCompleted && (
          <div className={styles.completedJourneyBanner}>
            <TrophyOutlined />
            <div>
              <Text strong style={{ fontSize: 14 }}>
                {selectedQuest.isChallenge
                  ? t('heatPumpSimulation.tutorialQuests.challengeCompleted', 'Challenge completed!')
                  : t('heatPumpSimulation.tutorialQuests.questCompleted', 'Quest completed!')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {selectedQuest.isChallenge
                  ? t('heatPumpSimulation.tutorialQuests.masteryDemonstrated', 'You demonstrated mastery!')
                  : t('heatPumpSimulation.tutorialQuests.greatWork', 'Great work!')}
              </Text>
            </div>
          </div>
        )}

        <div className={styles.actionButtonsRow}>
          <button
            onClick={handleStartJourney}
            className={styles.startJourneyBtn}
          >
            <PlayCircleOutlined className={styles.journeyIcon} />
            <span className={styles.journeyText}>
              {t('heatPumpSimulation.tutorialQuests.startJourney', 'Tool Guide')}
            </span>
          </button>

          {selectedQuestCompleted && !profileMode && (
            <button
              onClick={handleStartQuiz}
              className={styles.startQuizBtn}
            >
              <QuestionCircleOutlined className={styles.journeyIcon} />
              <span className={styles.journeyText}>{t('heatPumpSimulation.tutorialQuests.takeQuiz', 'Take the quiz')}</span>
            </button>
          )}

          {selectedQuestCompleted && (
            <Tooltip
              title={t('heatPumpSimulation.tutorialQuests.resetQuestTooltip', 'Clear this quest completion so you can run it again.')}
              color="white"
            >
              <button
                onClick={() => handleResetQuestProgress(selectedQuest.id)}
                className={styles.secondaryActionBtn}
              >
                <ReloadOutlined className={styles.journeyIcon} />
                <span className={styles.journeyText}>{t('heatPumpSimulation.tutorialQuests.resetQuest', 'Reset quest')}</span>
              </button>
            </Tooltip>
          )}

          {selectedQuestCompleted && nextQuest && (
            <button
              onClick={handleOpenNextQuest}
              className={`${styles.startJourneyBtn} ${styles.nextQuestBtn}`}
            >
              <span className={styles.journeyText}>{t('heatPumpSimulation.tutorialQuests.nextQuest', 'Next quest')}</span>
              <ArrowRightOutlined className={styles.journeyArrow} />
            </button>
          )}
        </div>

        {hasLearningObjectivesSection && (
          <div ref={(node) => setQuestSectionRef('learning-objectives', node)} className={`${styles.section} ${styles.questContentSection}`}>
            <Title level={5} style={{ fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
              <RocketOutlined style={{ marginRight: 6 }} />
              {t('trainingProfiles.learningObjectives', 'Learning objectives')}
            </Title>
            <RichContent html={selectedQuest.learningObjectivesRichContent}>
              <ul className={styles.objectivesListLeft}>
                {learningObjectives.filter(hasMeaningfulText).map((objective, idx) => (
                  <li key={idx}>{objective}</li>
                ))}
              </ul>
            </RichContent>
          </div>
        )}

        {hasScenarioSection && <Divider style={{ margin: '12px 0' }} />}

        {hasScenarioSection && (
          <div ref={(node) => setQuestSectionRef('scenario', node)} className={`${styles.section} ${styles.questContentSection}`}>
            <Title level={5} style={{ fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
              <ExperimentOutlined style={{ marginRight: 6 }} />
              {t('trainingProfiles.scenario', 'Scenario')}
            </Title>
            <div className={styles.scenarioBoxCompact}>
              <RichContent html={selectedQuest.scenarioRichContent}>
                <Paragraph style={{ marginBottom: 0, fontSize: 12, textAlign: 'left' }}>
                  {selectedQuest.scenario.context}
                </Paragraph>
              </RichContent>
            </div>
          </div>
        )}

        {hasStepsSection && <Divider style={{ margin: '12px 0' }} />}

        {hasStepsSection && (
          <div ref={(node) => setQuestSectionRef('steps', node)} className={`${styles.section} ${styles.questContentSection}`}>
            <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
              <AimOutlined style={{ marginRight: 6 }} />
              {`${t('trainingProfiles.stepsTitle', 'Steps')} (${visibleSteps.length})`}
            </Title>
            <Collapse
              ghost
              size="small"
              activeKey={expandedStepKeys}
              onChange={handleStepCollapseChange}
              className={styles.stepsCollapse}
              items={visibleSteps.map((step, idx) => {
                const isStepLocked = profileMode && lockedStepIds.has(String(step.id));
                const hasInstruction = hasRichContent(step.instructionRichContent) || hasMeaningfulText(step.instruction);
                const hasGuidance = hasRichContent(step.detailedGuidanceRichContent) || hasMeaningfulText(step.detailedGuidance);
                const hasSuccessIndicator = hasRichContent(step.successIndicatorRichContent) || hasMeaningfulText(step.successIndicator);
                const answerChecks = Array.isArray(step.answerChecks) ? step.answerChecks : [];
                const answerEntry = getStepAnswerEntry(step.id, answerChecks);
                const hasAnswerChallenge = profileMode && hasTrainingProfileStepAnswerChecks(step);
                const challengeSolved = answerEntry.solved;
                const challengeClosed = answerEntry.closed;
                const answeredCheckCount = answerChecks.filter((_, answerCheckIndex) => (answerEntry.checkStates[answerCheckIndex]?.attempts || 0) > 0).length;
                const closedCheckCount = answerChecks.filter((_, answerCheckIndex) => {
                  const currentCheckState = answerEntry.checkStates[answerCheckIndex] || createDefaultStepAnswerCheckState();
                  return currentCheckState.solved || currentCheckState.attempts >= STEP_ANSWER_MAX_ATTEMPTS;
                }).length;
                const challengeStatusText = challengeSolved
                  ? t('heatPumpSimulation.tutorialQuests.answerCorrect', 'All required answers confirmed.')
                  : challengeClosed
                    ? t('heatPumpSimulation.tutorialQuests.answerChecksClosed', 'Answer checks for this step are closed and the following steps are now available.')
                    : answerEntry.answered
                      ? t('heatPumpSimulation.tutorialQuests.answerChecksInProgress', 'Keep checking the remaining answers for this step.')
                      : null;

                return {
                  key: String(step.id),
                  className: isStepLocked ? styles.stepCollapseItemLocked : undefined,
                  collapsible: isStepLocked ? 'disabled' : 'header',
                  label: (
                    <div className={`${styles.stepCollapseHeader}${isStepLocked ? ` ${styles.stepCollapseHeaderLocked}` : ''}`}>
                      <span className={styles.stepPreviewNumber}>{idx + 1}</span>
                      <span className={styles.stepPreviewTask}>{step.task}</span>
                    </div>
                  ),
                  extra: renderJustInTimeTrigger(
                    hasRichContent(step.justInTimeRichContent)
                      ? {
                          id: `step-${selectedQuest.id}-${step.id}`,
                          title: `${t('trainingProfiles.stepsTitle', 'Steps')} ${idx + 1}: ${step.task}`,
                          html: step.justInTimeRichContent,
                        }
                      : null,
                    t('heatPumpSimulation.tutorialQuests.openStepHelp', 'Open step help')
                  ),
                  children: (
                    <div className={styles.stepCollapseContent}>
                      {isStepLocked && (
                        <div style={{ marginBottom: 12 }}>
                          <Text type="secondary">
                            {t(
                              'heatPumpSimulation.tutorialQuests.stepLockedUntilPreviousSolved',
                              'This step becomes available after the required answers above are correct or the attempt limit is reached.'
                            )}
                          </Text>
                        </div>
                      )}
                      {hasInstruction && (
                        <RichContent html={step.instructionRichContent}>
                          <p className={styles.stepInstruction}>{step.instruction}</p>
                        </RichContent>
                      )}
                      {hasGuidance && (
                        <RichContent html={step.detailedGuidanceRichContent}>
                          <p className={styles.stepGuidance}>{step.detailedGuidance}</p>
                        </RichContent>
                      )}
                      {hasSuccessIndicator && (
                        <div className={styles.stepSuccessHint}>
                          <BulbOutlined style={{ color: '#1677ff', marginRight: 6, marginTop: 1 }} />
                          <RichContent html={step.successIndicatorRichContent}>
                            <span>{step.successIndicator}</span>
                          </RichContent>
                        </div>
                      )}
                      {hasAnswerChallenge && (
                        <div style={{ marginTop: 12, padding: 12, border: '1px solid #d9e6f7', borderRadius: 10, background: '#f7fbff' }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            {t('heatPumpSimulation.tutorialQuests.answerChecksTitle', 'Required answers')}
                          </Text>
                          <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            {answerChecks.map((answerCheck, answerCheckIndex) => (
                              <div key={answerCheck.id || answerCheckIndex} style={{ padding: '10px 0', borderTop: answerCheckIndex === 0 ? 'none' : '1px solid #e8eef7' }}>
                                {(() => {
                                  const currentCheckState = answerEntry.checkStates[answerCheckIndex] || createDefaultStepAnswerCheckState();
                                  const currentAnswerValue = answerEntry.answers[answerCheckIndex];
                                  const answerCheckClosed = currentCheckState.solved || currentCheckState.attempts >= STEP_ANSWER_MAX_ATTEMPTS;
                                  const answerCheckRevealed = !currentCheckState.solved && currentCheckState.attempts >= STEP_ANSWER_MAX_ATTEMPTS;
                                  const answerCheckAttemptsRemaining = Math.max(STEP_ANSWER_MAX_ATTEMPTS - currentCheckState.attempts, 0);
                                  const answerCheckHasValue = isAnswerCheckValueProvided(answerCheck, currentAnswerValue);
                                  const answerStatusText = currentCheckState.solved
                                    ? t('heatPumpSimulation.tutorialQuests.answerCheckCorrect', 'Answer confirmed.')
                                    : currentCheckState.lastSubmissionCorrect === false && answerCheckRevealed
                                      ? t('heatPumpSimulation.tutorialQuests.answerCheckRevealUnlocked', 'Attempt limit reached for this answer. The correct answer is shown below.')
                                      : currentCheckState.lastSubmissionCorrect === false
                                        ? t('heatPumpSimulation.tutorialQuests.answerCheckAttemptsRemaining', '{{attemptsRemaining}} attempts remaining for this answer.', {
                                          attemptsRemaining: answerCheckAttemptsRemaining,
                                        })
                                        : null;

                                  return (
                                    <>
                                      {answerCheck.type === 'yesNoQuestion' ? (
                                        <>
                                          <div className={styles.answerCheckHeaderRow}>
                                            <Text strong className={styles.answerCheckHeaderText}>
                                              {answerCheck.question}
                                            </Text>
                                            {renderJustInTimeTrigger(
                                              hasRichContent(answerCheck.justInTimeRichContent)
                                                ? {
                                                    id: `answer-check-${selectedQuest.id}-${step.id}-${answerCheck.id || answerCheckIndex}`,
                                                    title: `${step.task}`,
                                                    html: answerCheck.justInTimeRichContent,
                                                  }
                                                : null,
                                              t('heatPumpSimulation.tutorialQuests.openAnswerCheckHelp', 'Open answer-check help')
                                            )}
                                          </div>
                                          <Select
                                            value={getYesNoSelectValue(currentAnswerValue)}
                                            onChange={(value) => updateStepAnswerValue(step.id, answerCheckIndex, value)}
                                            options={[
                                              { value: YES_NO_SELECT_VALUES.yes, label: t('common.yes', 'Yes') },
                                              { value: YES_NO_SELECT_VALUES.no, label: t('common.no', 'No') },
                                            ]}
                                            placeholder={t('heatPumpSimulation.tutorialQuests.selectYesNoPlaceholder', 'Select yes or no')}
                                            disabled={answerCheckClosed}
                                            style={{ width: '100%' }}
                                          />
                                        </>
                                      ) : answerCheck.type === 'multipleChoice' ? (
                                        <>
                                          <div className={styles.answerCheckHeaderRow}>
                                            <Text strong className={styles.answerCheckHeaderText}>
                                              {answerCheck.question}
                                            </Text>
                                            {renderJustInTimeTrigger(
                                              hasRichContent(answerCheck.justInTimeRichContent)
                                                ? {
                                                    id: `answer-check-${selectedQuest.id}-${step.id}-${answerCheck.id || answerCheckIndex}`,
                                                    title: `${step.task}`,
                                                    html: answerCheck.justInTimeRichContent,
                                                  }
                                                : null,
                                              t('heatPumpSimulation.tutorialQuests.openAnswerCheckHelp', 'Open answer-check help')
                                            )}
                                          </div>
                                          {getMultipleChoiceCorrectOptionIndexes(answerCheck).length > 1 ? (
                                            <>
                                              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                                                {t('heatPumpSimulation.tutorialQuests.selectMultipleChoicesHint', 'Select all correct choices.')}
                                              </Text>
                                              <Checkbox.Group
                                                value={normalizeMultipleChoiceSelection(currentAnswerValue)}
                                                onChange={(value) => updateStepAnswerValue(step.id, answerCheckIndex, normalizeMultipleChoiceSelection(value))}
                                                disabled={answerCheckClosed}
                                                style={{ width: '100%' }}
                                              >
                                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                                  {(Array.isArray(answerCheck.options) ? answerCheck.options : []).map((option, optionIndex) => (
                                                    <Checkbox key={`${answerCheck.id || answerCheckIndex}-${optionIndex}`} value={optionIndex}>
                                                      {`${option}`}
                                                    </Checkbox>
                                                  ))}
                                                </Space>
                                              </Checkbox.Group>
                                            </>
                                          ) : (
                                            <Select
                                              className={styles.answerCheckSelect}
                                              popupClassName={styles.answerCheckSelectDropdown}
                                              value={normalizeMultipleChoiceSelection(currentAnswerValue)[0]}
                                              onChange={(value) => updateStepAnswerValue(step.id, answerCheckIndex, value)}
                                              options={(Array.isArray(answerCheck.options) ? answerCheck.options : []).map((option, optionIndex) => ({
                                                value: optionIndex,
                                                label: `${optionIndex + 1}. ${option}`,
                                              }))}
                                              placeholder={t('heatPumpSimulation.tutorialQuests.selectMultipleChoicePlaceholder', 'Select the correct choice')}
                                              disabled={answerCheckClosed}
                                              style={{ width: '100%' }}
                                            />
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className={styles.answerCheckHeaderRow}>
                                            <Text strong className={styles.answerCheckHeaderText}>
                                              {t('heatPumpSimulation.tutorialQuests.enterExpectedValue', 'Enter value for {{label}}', {
                                                label: answerCheck.name,
                                              })}
                                            </Text>
                                            {renderJustInTimeTrigger(
                                              hasRichContent(answerCheck.justInTimeRichContent)
                                                ? {
                                                    id: `answer-check-${selectedQuest.id}-${step.id}-${answerCheck.id || answerCheckIndex}`,
                                                    title: `${step.task}`,
                                                    html: answerCheck.justInTimeRichContent,
                                                  }
                                                : null,
                                              t('heatPumpSimulation.tutorialQuests.openAnswerCheckHelp', 'Open answer-check help')
                                            )}
                                          </div>
                                          <Input
                                            value={currentAnswerValue ?? ''}
                                            onChange={(event) => updateStepAnswerValue(step.id, answerCheckIndex, event.target.value)}
                                            placeholder={t('heatPumpSimulation.tutorialQuests.enterValuePlaceholder', 'Type the calculated value')}
                                            disabled={answerCheckClosed}
                                            onPressEnter={() => {
                                              if (answerCheckHasValue) {
                                                handleCheckSingleStepAnswer(step, answerCheckIndex);
                                              }
                                            }}
                                          />
                                        </>
                                      )}
                                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <Button
                                          type="primary"
                                          onClick={() => handleCheckSingleStepAnswer(step, answerCheckIndex)}
                                          disabled={answerCheckClosed || !answerCheckHasValue}
                                        >
                                          {t('heatPumpSimulation.tutorialQuests.checkAnswer', 'Check answer')}
                                        </Button>
                                        <Text type="secondary">
                                          {t('heatPumpSimulation.tutorialQuests.attemptCount', 'Attempts: {{attempts}} / {{max}}', {
                                            attempts: currentCheckState.attempts,
                                            max: STEP_ANSWER_MAX_ATTEMPTS,
                                          })}
                                        </Text>
                                      </div>
                                      {answerStatusText && (
                                        <div style={{ marginTop: 8 }}>
                                          <Text type={currentCheckState.solved ? 'success' : answerCheckRevealed ? 'warning' : 'danger'}>
                                            {answerStatusText}
                                          </Text>
                                        </div>
                                      )}
                                      {answerCheckRevealed && (
                                        <div style={{ marginTop: 8 }}>
                                          <Text type="secondary">
                                            {answerCheck.type === 'yesNoQuestion'
                                              ? t('heatPumpSimulation.tutorialQuests.yesNoReveal', '{{question}}: {{answer}}', {
                                                question: answerCheck.question,
                                                answer: answerCheck.correct ? t('common.yes', 'Yes') : t('common.no', 'No'),
                                              })
                                              : answerCheck.type === 'multipleChoice'
                                                ? t('heatPumpSimulation.tutorialQuests.multipleChoiceReveal', '{{question}}: {{answer}}', {
                                                  question: answerCheck.question,
                                                  answer: getMultipleChoiceRevealText(answerCheck),
                                                })
                                                : t('heatPumpSimulation.tutorialQuests.expectedValueReveal', '{{label}}: {{value}}', {
                                                  label: answerCheck.name,
                                                  value: answerCheck.value,
                                                })}
                                          </Text>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            ))}
                          </Space>
                          {!answerEntry.answered && !challengeClosed && (
                            <div style={{ marginTop: 8 }}>
                              <Text type="secondary">
                                {t('heatPumpSimulation.tutorialQuests.completeAllAnswersBeforeCheck', 'Complete all required answers before checking.')}
                              </Text>
                            </div>
                          )}
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">
                              {t('heatPumpSimulation.tutorialQuests.answerChecksProgress', '{{answered}} / {{total}} answer checks answered, {{closed}} / {{total}} checked to completion.', {
                                answered: answeredCheckCount,
                                closed: closedCheckCount,
                                total: answerChecks.length,
                              })}
                            </Text>
                          </div>
                          {challengeStatusText && (
                            <div style={{ marginTop: 8 }}>
                              <Text type={challengeSolved ? 'success' : challengeClosed ? 'warning' : 'secondary'}>
                                {challengeStatusText}
                              </Text>
                            </div>
                          )}
                          {step.lockNextStepsUntilCorrect && lockedStepCount > 0 && !challengeClosed && (
                            <div style={{ marginTop: 8 }}>
                              <Text type="secondary">
                                {t('heatPumpSimulation.tutorialQuests.laterStepsUnavailable', '{{count}} later step(s) stay unavailable until these answers are correct or the attempt limit is reached.', {
                                  count: lockedStepCount,
                                })}
                              </Text>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                };
              })}
            />
            {lockedStepCount > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  {t('heatPumpSimulation.tutorialQuests.stepsUnavailableBecauseOfChallenge', '{{count}} later step(s) are currently unavailable because of a calculation challenge.', {
                    count: lockedStepCount,
                  })}
                </Text>
              </div>
            )}
          </div>
        )}

        {hasCompletionSection && <Divider style={{ margin: '12px 0' }} />}
        {hasCompletionSection && (
          <div ref={(node) => setQuestSectionRef('completion', node)} className={`${styles.section} ${styles.questContentSection}`}>
            <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
              <CheckCircleFilled style={{ marginRight: 6, color: isQuestCompleted(selectedQuest.id) ? "#52c41a" : "#d9d9d9" }} />
              {t('heatPumpSimulation.tutorialQuests.completion', 'Completion')}
            </Title>
            <div className={styles.criteriaBoxCompact}>
              <ul className={styles.criteriaListCompact}>
                {completionConditionItems.map((condition, idx) => {
                  const conditionMet = storedQuestConditionStatus[selectedQuest.id]?.[idx] ?? false;
                  const isAnswerCheckCondition = condition.type === 'answerChecks';
                  return (
                    <li key={`${condition.type}-${idx}`} className={isAnswerCheckCondition ? styles.criteriaListCompactAnswerChecks : undefined}>
                      <CheckCircleFilled style={{
                        color: isAnswerCheckCondition
                          ? conditionMet ? "#1677ff" : "#d9e8ff"
                          : conditionMet ? "#52c41a" : "#d9d9d9",
                        marginRight: 6,
                        fontSize: 12
                      }} />
                      {condition.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {hasKnowledgeSynthesisSection && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div ref={(node) => setQuestSectionRef('key-takeaways', node)} className={`${styles.section} ${styles.questContentSection}`}>
              <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
                <BookOutlined style={{ marginRight: 6 }} />
                {t('heatPumpSimulation.tutorialQuests.keyTakeaways', 'Key takeaways')}
              </Title>
              <div className={styles.synthesisBoxCompact}>
                <RichContent html={selectedQuest.knowledgeSynthesisRichContent}>
                  <ul className={styles.takeawaysList}>
                    {selectedQuest.knowledgeSynthesis.keyTakeaways.filter(hasMeaningfulText).slice(0, 3).map((takeaway, idx) => (
                      <li key={idx}>{takeaway}</li>
                    ))}
                  </ul>
                  {hasMeaningfulText(selectedQuest.knowledgeSynthesis.formulaHighlight) && (
                    <div className={styles.formulaBoxCompact}>
                      <pre className={styles.formulaTextCompact}>
                        {selectedQuest.knowledgeSynthesis.formulaHighlight.split('\n')[0]}
                      </pre>
                    </div>
                  )}
                </RichContent>
              </div>
            </div>
          </>
        )}
        </div>

        {shouldShowQuestSectionNav && (
          <div className={styles.questSectionNavSticky}>
            <nav className={styles.questSectionNavRail} aria-label={t('heatPumpSimulation.tutorialQuests.questSectionNavigation', 'Quest section navigation')}>
              {questContentSectionItems.map((sectionItem) => (
                <Tooltip key={sectionItem.id} title={sectionItem.label} placement="left" color="white">
                  <button
                    type="button"
                    className={`${styles.questSectionNavButton}${activeQuestSectionId === sectionItem.id ? ` ${styles.questSectionNavButtonActive}` : ''}${sectionItem.id === 'completion' ? ` ${styles.questSectionNavButtonCompletion}` : ''}`}
                    onClick={() => handleQuestSectionNavClick(sectionItem.id)}
                    aria-label={sectionItem.label}
                  >
                    <span className={styles.questSectionNavDash} />
                  </button>
                </Tooltip>
              ))}
            </nav>
          </div>
        )}
      </div>
    );
  };

  const dockedInformationContent = hasProfileInformation ? (
    <div className={styles.dockedInfoPanel}>
      <div className={styles.dockedInfoContent}>
        <RichContent html={trainingProfile?.informationRichContent} />
      </div>
    </div>
  ) : null;

  const handleEmptyQuestStateClick = useCallback(() => {
    if (!hasAnyQuests) {
      return;
    }

    document.documentElement.style.removeProperty('--heatcraft-docked-quest-hint-id');

    if (QUESTS[0]) {
      handleQuestClick(QUESTS[0]);
    }
  }, [QUESTS, handleQuestClick, hasAnyQuests]);

  const handleEmptyQuestStateHover = useCallback((isHovered) => {
    if (!hasAnyQuests) {
      return;
    }

    const nextQuestId = isHovered ? String(QUESTS[0]?.id || '') : '';
    document.documentElement.style.setProperty('--heatcraft-docked-quest-hint-id', nextQuestId);
  }, [QUESTS, hasAnyQuests]);

  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty('--heatcraft-docked-quest-hint-id');
    };
  }, []);

  const dockedQuestEmptyState = hasAnyQuests ? (
    <button
      type="button"
      className={styles.dockedEmptyStateAction}
      onClick={handleEmptyQuestStateClick}
      onMouseEnter={() => handleEmptyQuestStateHover(true)}
      onMouseLeave={() => handleEmptyQuestStateHover(false)}
    >
      <BulbOutlined className={styles.dockedEmptyStateInlineIcon} />
      <span>{t('heatPumpSimulation.noQuestSelectedShort', 'Choose a quest')}</span>
    </button>
  ) : (
    <div className={styles.dockedEmptyStatePlain}>
      {t('heatPumpSimulation.noQuests', 'No quests')}
    </div>
  );

  const dockedTabItems = [
    ...(hasProfileInformation ? [{
      key: 'information',
      label: t('heatPumpSimulation.informationTab', 'Information'),
      children: dockedInformationContent,
    }] : []),
    {
      key: 'controls',
      label: t('heatPumpSimulation.controlsTab', 'Controls'),
      children: <div className={styles.dockedControlsTab}>{controlsContent}</div>,
    },
    {
      key: 'quest',
      label: t('heatPumpSimulation.questTab', 'Quest'),
      children: selectedQuest ? (
        <div className={styles.dockedQuestPanel}>
          {renderSelectedQuestHeader({ docked: true })}
          <div ref={questScrollContainerRef} className={styles.dockedQuestContent}>
            {renderSelectedQuestPanelBody()}
          </div>
        </div>
      ) : dockedQuestEmptyState,
    },
  ];

  const dockedPanelContent = isDockedLayout ? (
    <div className={styles.dockedWorkspace}>
      <div className={styles.dockedMainPanel}>
        <Tabs
          activeKey={activeDockedTab}
          onChange={handleDockedTabChange}
          items={dockedTabItems}
          className={styles.dockedTabs}
        />
      </div>
      {justInTimePanel && (
        <div
          className={styles.dockedJustInTimePanel}
          style={{ width: JUST_IN_TIME_PANEL_WIDTH_PRESETS[justInTimePanelWidthPreset] || JUST_IN_TIME_PANEL_PREFERRED_WIDTH }}
        >
          <div className={styles.floatingJustInTimePanelHeader}>
            <div className={styles.floatingPanelHeaderMain}>
              <div className={styles.floatingJustInTimePanelTitle}>{justInTimePanel.title}</div>
            </div>
            <div className={styles.floatingPanelHeaderControls}>
              <Space size={4} className={styles.floatingPanelWidthControls}>
                {widthPresetOptions.map((option) => (
                  <Tooltip key={`docked-jit-${option.key}`} title={option.tooltip} color="white">
                    <Button
                      type="text"
                      size="small"
                      icon={<QuestPanelWidthIcon preset={option.key} />}
                      className={`${styles.floatingPanelHeaderIconButton}${justInTimePanelWidthPreset === option.key ? ` ${styles.floatingPanelHeaderIconButtonActive}` : ''}`}
                      onClick={() => setJustInTimePanelWidthPreset(option.key)}
                    />
                  </Tooltip>
                ))}
              </Space>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCloseJustInTimePanel}
              />
            </div>
          </div>
          <div className={styles.floatingJustInTimePanelContent}>
            <RichContent html={justInTimePanel.html} />
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      {showCompletionCelebration && selectedQuest && (
        <div className={styles.questCompletionOverlay} aria-hidden="true">
          <div className={styles.questCompletionOverlayDecor}>
            <span className={`${styles.questCompletionSpark} ${styles.questCompletionOverlaySparkA}`}>✦</span>
            <span className={`${styles.questCompletionSpark} ${styles.questCompletionOverlaySparkB}`}>✦</span>
            <span className={`${styles.questCompletionSpark} ${styles.questCompletionOverlaySparkC}`}>✦</span>
            <TrophyOutlined className={styles.questCompletionOverlayCup} />
          </div>
          <div className={styles.questCompletionOverlayText}>
            <div className={styles.questCompletionOverlayTitle}>
              <span className={styles.questCompletionOverlayTitleLine}>
                {t('heatPumpSimulation.tutorialQuests.questCompleted', 'Quest completed!')}
              </span>
            </div>
            <div className={styles.questCompletionOverlayMessage}>
              <span className={styles.questCompletionOverlayMessageLine}>
                {t('heatPumpSimulation.tutorialQuests.questCompletedMessage', 'Excellent work! You completed "{{title}}".', {
                  title: selectedQuest.title,
                })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quest Buttons Row */}
      <div className={styles.questContainer}>
        {/* User Type Selector */}
        {!profileMode && <div className={styles.userTypeSelector}>
          <Select
            value={userType}
            onChange={handleUserTypeChange}
            size="small"
            style={{ width: 155 }}
            popupMatchSelectWidth={false}
            options={userTypeOptions.map(opt => ({
              value: opt.value,
              label: (
                <Space size={6}>
                  {opt.icon}
                  <span>{opt.label}</span>
                </Space>
              )
            }))}
          />
        </div>}
        
        <div className={styles.questButtons}>
          {hasProfileInformation && (
            <InformationButton
              active={isDockedLayout && activeDockedTab === 'information'}
              animate={showInformationPulse}
              onClick={handleInformationClick}
            />
          )}
          {QUESTS.map((quest) => (
            <QuestButton
              key={quest.id}
              quest={quest}
              isCompleted={isQuestCompleted(quest.id)}
              isActive={selectedQuest?.id === quest.id || (!selectedQuest && quest.id === activeQuestId)}
              onClick={() => handleQuestClick(quest)}
            />
          ))}
        </div>
        <div className={styles.progressSection}>
          <Progress 
            percent={progressPercent} 
            size="small" 
            style={{ width: 60 }}
            strokeColor={{
              '0%': '#1890ff',
              '100%': '#52c41a',
            }}
          />
          <span className={styles.progressText}>
            {completedCount}/{QUESTS.length}
          </span>
        </div>
        {profileMode && profileQuizAssignments.length > 0 && <div className={styles.mindMapSection} style={{ position: 'relative' }}>
          <Tooltip title={t('heatPumpSimulation.tutorialQuests.assignedSkillQuizTooltip', 'Choose an assigned skill quiz')} color="white">
            <button
              onClick={handleProfileSkillQuizClick}
              className={styles.mindMapButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="5" cy="6" r="2.5" fill="#ffd666" />
                <circle cx="19" cy="6" r="2.5" fill="#95de64" />
                <circle cx="5" cy="18" r="2.5" fill="#ff9c6e" />
                <circle cx="19" cy="18" r="2.5" fill="#b37feb" />
                <path d="M8.5 9.5L6.5 7.5M15.5 9.5L17.5 7.5M8.5 14.5L6.5 16.5M15.5 14.5L17.5 16.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{t('heatPumpSimulation.skillTest', 'Skill Test')}</span>
            </button>
          </Tooltip>
        </div>}

        {!profileMode && <div className={styles.mindMapSection}>
          <Tooltip title={t('heatPumpSimulation.tutorialQuests.skillTestTooltip', 'Take the skill test to assess your knowledge')} color="white">
            <button 
              onClick={() => setShowProfessionalQuiz(true)}
              className={styles.mindMapButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Central node */}
                <circle cx="12" cy="12" r="4" fill="#fff" />
                {/* Branch nodes */}
                <circle cx="5" cy="6" r="2.5" fill="#ffd666" />
                <circle cx="19" cy="6" r="2.5" fill="#95de64" />
                <circle cx="5" cy="18" r="2.5" fill="#ff9c6e" />
                <circle cx="19" cy="18" r="2.5" fill="#b37feb" />
                {/* Connecting lines */}
                <path d="M8.5 9.5L6.5 7.5M15.5 9.5L17.5 7.5M8.5 14.5L6.5 16.5M15.5 14.5L17.5 16.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{t('heatPumpSimulation.skillTest', 'Skill Test')}</span>
            </button>
          </Tooltip>
        </div>}
      </div>

      {/* Spotlight/Tour Overlay - Active during journey mode */}
      <TutorialSpotlight
        isActive={isJourneyActive}
        quest={selectedQuest}
        currentStepIndex={journeyStepIndex}
        onNext={handleJourneyNext}
        onPrevious={handleJourneyPrevious}
        onClose={handleJourneyClose}
        onComplete={handleJourneyComplete}
        isQuestCompleted={selectedQuest ? isQuestCompleted(selectedQuest.id) : false}
      />

      {isDockedLayout && !dockedPanelTarget && dockedPanelContent}
      {isDockedLayout && dockedPanelTarget && createPortal(dockedPanelContent, dockedPanelTarget)}

      {/* Floating Quest Panel */}
      {!isDockedLayout && panelVisible && selectedQuest && !isMinimized && (
        <>
          {justInTimePanel && (
            <div
              className={styles.floatingJustInTimePanel}
              style={{
                left: justInTimePanelLeft,
                top: position.y,
                width: floatingJustInTimeWidth,
                height: panelSize.height || undefined,
              }}
            >
              <div className={styles.floatingJustInTimePanelHeader}>
                <div className={styles.floatingPanelHeaderMain}>
                  <div className={styles.floatingJustInTimePanelTitle}>{justInTimePanel.title}</div>
                </div>
                <div className={styles.floatingPanelHeaderControls}>
                  <Space size={4} className={styles.floatingPanelWidthControls}>
                    {widthPresetOptions.map((option) => (
                      <Tooltip key={`jit-${option.key}`} title={option.tooltip} color="white">
                        <Button
                          type="text"
                          size="small"
                          icon={<QuestPanelWidthIcon preset={option.key} />}
                          className={`${styles.floatingPanelHeaderIconButton}${justInTimePanelWidthPreset === option.key ? ` ${styles.floatingPanelHeaderIconButtonActive}` : ''}`}
                          onClick={() => setJustInTimePanelWidthPreset(option.key)}
                        />
                      </Tooltip>
                    ))}
                  </Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={handleCloseJustInTimePanel}
                  />
                </div>
              </div>
              <div className={styles.floatingJustInTimePanelContent}>
                <RichContent html={justInTimePanel.html} />
              </div>
            </div>
          )}

          <div 
            ref={dragRef}
            className={styles.floatingPanel}
            style={{
              width: floatingPanelWidth,
              left: position.x,
              top: position.y,
              cursor: isDragging ? 'grabbing' : 'default'
            }}
          >
            {renderSelectedQuestHeader()}
            <div ref={questScrollContainerRef} className={styles.floatingPanelContent}>
              {renderSelectedQuestPanelBody()}
            </div>
        </div>
        </>
      )}

      {/* Minimized Floating Tab */}
      {!isDockedLayout && panelVisible && isMinimized && !isJourneyActive && (
        <div 
          className={styles.minimizedTab}
          style={{ left: position.x, top: position.y }}
        >
          <Space size="small">
            {selectedQuest?.icon}
            <Text strong style={{ fontSize: 12 }}>{selectedQuest?.title}</Text>
          </Space>
          <Space size={4}>
            <Tooltip title={t('heatPumpSimulation.tutorialQuests.expand', 'Expand')} color="white">
              <Button 
                type="text" 
                size="small" 
                icon={<ExpandOutlined />} 
                onClick={() => setIsMinimized(false)}
              />
            </Tooltip>
            <Button 
              type="text" 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={handleClosePanel}
            />
          </Space>
        </div>
      )}

      {/* Professional Quiz Modal */}
      <ProfessionalQuiz 
        isOpen={showProfessionalQuiz}
        onClose={() => setShowProfessionalQuiz(false)}
      />

      <AssignedSkillQuizModal
        isOpen={showAssignedSkillQuiz}
        onClose={() => setShowAssignedSkillQuiz(false)}
        assignments={profileQuizAssignments}
      />
    </>
  );
};

TutorialQuests.propTypes = {
  isSimulationRunning: PropTypes.bool.isRequired,
  outdoorTemperature: PropTypes.number.isRequired,
  desiredTemperature: PropTypes.number.isRequired,
  selectedRoomType: PropTypes.string.isRequired,
  selectedCity: PropTypes.string,
  roofArea: PropTypes.number.isRequired,
  wallArea: PropTypes.number.isRequired,
  windowArea: PropTypes.number.isRequired,
  uValue: PropTypes.number.isRequired,
  totalArea: PropTypes.number.isRequired,
  airChangeRate: PropTypes.number.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  heatOutput: PropTypes.number.isRequired,
  envelopeLoss: PropTypes.number.isRequired,
  ventilationLoss: PropTypes.number.isRequired,
  valvePosition: PropTypes.number.isRequired,
  thermostatLevel: PropTypes.number.isRequired,
  currentCOP: PropTypes.number.isRequired,
  currentTemperature: PropTypes.number.isRequired,
  radiatorType: PropTypes.string.isRequired,
  radiatorCount: PropTypes.number.isRequired,
  systemPressure: PropTypes.number.isRequired,
  trainingProfile: PropTypes.object,
  trainingProfileMode: PropTypes.bool,
  layoutMode: PropTypes.oneOf(['floating', 'docked']),
  controlsContent: PropTypes.node,
  dockedPanelTarget: PropTypes.instanceOf(Element),
};

export default TutorialQuests;
